import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { useAuthStore } from '@/store/auth'
import { authApi } from '@/lib/api'
import Layout from '@/components/Layout'
import Login from '@/pages/Login'
import AuthCallback from '@/pages/AuthCallback'
import Dashboard from '@/pages/Dashboard'
import NewAssessment from '@/pages/NewAssessment'
import AssessmentResults from '@/pages/AssessmentResults'
import AssessmentsHistory from '@/pages/AssessmentsHistory'
import AuditLog from '@/pages/AuditLog'
import ReviewQueue from '@/pages/ReviewQueue'
import Profile from '@/pages/Profile'
import SchoolAdmin from '@/pages/SchoolAdmin'
import DpoDashboard from '@/pages/DpoDashboard'
import AdminPanel from '@/pages/AdminPanel'
import Maintenance from '@/pages/Maintenance'
import Guida from '@/pages/Guida'
import Registro from '@/pages/Registro'
import Documenti from '@/pages/Documenti'
import Governance from '@/pages/Governance'
import MaintenanceGate from '@/components/MaintenanceGate'

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore()
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <>{children}</>
}

function AppInitializer({ children }: { children: React.ReactNode }) {
  const { setUser, logout, isAuthenticated } = useAuthStore()

  const { data: user, isLoading, isError } = useQuery({
    queryKey: ['me'],
    queryFn: authApi.getMe,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 1000 * 60 * 5,
  })

  useEffect(() => {
    if (user) setUser(user)
    if (isError) logout()
  }, [user, isError, setUser, logout])

  // Block rendering until the initial auth check completes
  if (isAuthenticated && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="w-8 h-8 border-4 border-mim-blue border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return <>{children}</>
}

export default function App() {
  return (
    <BrowserRouter>
      <AppInitializer>
        <MaintenanceGate>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="assessment/new" element={<NewAssessment />} />
              <Route path="assessment/:id" element={<AssessmentResults />} />
              <Route path="registro" element={<Registro />} />
              <Route path="documenti" element={<Documenti />} />
              <Route path="governance" element={<Governance />} />
              <Route path="history" element={<AssessmentsHistory />} />
              <Route path="review-queue" element={<ReviewQueue />} />
              <Route path="audit" element={<AuditLog />} />
              <Route path="profile" element={<Profile />} />
              <Route path="school" element={<SchoolAdmin />} />
              <Route path="dpo" element={<DpoDashboard />} />
              <Route path="admin" element={<AdminPanel />} />
              <Route path="manutenzione" element={<Maintenance />} />
              <Route path="guida" element={<Guida />} />
              <Route path="guida/:slug" element={<Guida />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </MaintenanceGate>
      </AppInitializer>
      <Toaster />
    </BrowserRouter>
  )
}
